'use client'

import { Cell, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { EducationType } from '@marketplace-web/conversation-education/conversation-education-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import ProgressStepIconExperimental from '../../ProgressStepIconExperimental'

type Props = {
  type: EducationType
  price: string | null
}

type Step = {
  completed: boolean
  active: boolean
  title: string
  description?: string
  testId?: string
}

const ElectronicsSteps = ({ type, price }: Props) => {
  const translate = useTranslate('conversation.education')
  const firstTimeElectronicsEvsModalsImprovements = useAbTest(
    'first_time_electronics_and_evs_seller_modals_improvement',
  )

  const isAbTestOn = firstTimeElectronicsEvsModalsImprovements?.variant === 'on'

  const isEvsCheckVisible = [
    EducationType.InConversationSoldElectronicsEvs,
    EducationType.InConversationTransactionCompletedElectronicsEvs,
  ].includes(type)

  const isTransactionCompleted = [
    EducationType.InConversationTransactionCompletedElectronics,
    EducationType.InConversationTransactionCompletedElectronicsEvs,
  ].includes(type)

  const formatSoldStep = () => {
    let description
    let testId = 'education-sold-step'

    if (!isAbTestOn) {
      if (isTransactionCompleted) {
        description = translate('steps.sold.completed_description')
        testId = 'education-sold-step-completed'
      } else if (type === EducationType.InConversationSoldElectronicsEvs) {
        description = translate('steps.electronics.sold.evs_description')
        testId = 'education-sold-step-evs'
      } else {
        description = translate('steps.sold.description', { price })
      }
    }

    return {
      completed: true,
      active: false,
      title: translate('steps.sold.title'),
      description: description ?? '',
      testId,
    }
  }

  const formatSendItStep = () => {
    const completed = isTransactionCompleted
    const active = !isTransactionCompleted

    if (type === EducationType.InConversationTransactionCompletedElectronicsEvs && isAbTestOn) {
      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.factory_reset_title'),
        testId: 'education-send-it-step-sold-electronics-evs',
      }
    }

    if (type === EducationType.InConversationSoldElectronics) {
      if (isAbTestOn) {
        return {
          completed,
          active,
          title: translate('steps.electronics.send_it.factory_reset_title'),
          description: translate('steps.electronics.send_it.factory_reset_body'),
          testId: 'education-send-it-step-sold-electronics-evs',
        }
      }

      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.sold_title'),
        description: translate('steps.electronics.send_it.sold_body'),
        testId: 'education-send-it-step-sold-electronics',
      }
    }

    if (type === EducationType.InConversationSoldElectronicsEvs) {
      if (isAbTestOn) {
        return {
          completed,
          active,
          title: translate('steps.electronics.send_it.factory_reset_title'),
          description: translate('steps.electronics.send_it.factory_reset_body'),
          testId: 'education-send-it-step-sold-electronics-evs',
        }
      }

      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.sold_evs_title'),
        description: translate('steps.electronics.send_it.sold_evs_body'),
        testId: 'education-send-it-step-sold-electronics-evs',
      }
    }

    if (type === EducationType.InConversationTransactionCompletedElectronics) {
      if (isAbTestOn) {
        return {
          completed,
          active,
          title: translate('steps.electronics.send_it.factory_reset_title'),
          testId: 'education-send-it-step-sold-electronics-evs',
        }
      }

      return {
        completed,
        active,
        title: translate('steps.electronics.send_it.completed_title'),
        description: translate('steps.electronics.send_it.completed_body'),
        testId: 'education-send-it-step-completed-electronics',
      }
    }

    return {
      completed,
      active,
      title: translate('steps.electronics.send_it.completed_evs_title'),
      description: translate('steps.electronics.send_it.completed_evs_body'),
      testId: 'education-send-it-step-completed-electronics-evs',
    }
  }

  const formatEvsCheckStep = () => {
    if (type === EducationType.InConversationTransactionCompletedElectronicsEvs && isAbTestOn) {
      return {
        completed: isTransactionCompleted,
        active: false,
        title: translate('steps.electronics.evs_sold_step.title'),
        testId: 'education-evs-step-completed',
      }
    }

    if (type === EducationType.InConversationSoldElectronicsEvs) {
      if (isAbTestOn) {
        return {
          completed: isTransactionCompleted,
          active: false,
          title: translate('steps.electronics.evs_sold_step.title'),
          testId: 'education-evs-step-sold',
        }
      }

      return {
        completed: isTransactionCompleted,
        active: false,
        title: translate('steps.electronics.evs_sold_step.title'),
        description: translate('steps.electronics.evs_sold_step.body'),
        testId: 'education-evs-step-sold',
      }
    }

    return {
      completed: isTransactionCompleted,
      active: false,
      title: translate('steps.authenticity_check.title'),
      description: translate('steps.authenticity_check.completed_description'),
      testId: 'education-evs-step-completed',
    }
  }

  const formatPackEvsItemStep = () => {
    if (
      type === EducationType.InConversationTransactionCompletedElectronicsEvs ||
      type === EducationType.InConversationTransactionCompletedElectronics
    ) {
      return {
        completed: true,
        active: true,
        title: translate('steps.electronics.send_it.pack_and_ship_title'),
        testId: 'education-send-it-step-completed-electronics',
      }
    }

    return {
      completed: false,
      active: true,
      title: translate('steps.electronics.send_it.pack_and_ship_title'),
      description: translate('steps.electronics.send_it.pack_and_ship_body'),
      testId: 'education-pack-and-ship-step-electronics',
    }
  }

  const formatGetPaidStep = () => {
    if (isAbTestOn) {
      return {
        completed: false,
        active: isTransactionCompleted,
        title: translate('steps.get_paid.title'),
        description: isTransactionCompleted
          ? translate('steps.get_paid.completed_description')
          : '',
        testId: 'education-get-paid-step-electronics',
      }
    }

    return {
      completed: false,
      active: isTransactionCompleted,
      title: translate('steps.get_paid.title'),
      description: isTransactionCompleted
        ? translate('steps.get_paid.completed_description')
        : translate('steps.get_paid.description'),
      testId: 'education-get-paid-step-electronics',
    }
  }

  const saleSteps = [
    formatSoldStep(),
    formatSendItStep(),
    isAbTestOn && formatPackEvsItemStep(),
    ...(isEvsCheckVisible ? [formatEvsCheckStep()] : []),
    formatGetPaidStep(),
  ]

  const getStepIconStatus = (completed: boolean, active: boolean) => {
    if (completed) return ProgressStepIconExperimental.Status.Completed

    if (active) return ProgressStepIconExperimental.Status.Active

    return ProgressStepIconExperimental.Status.Inactive
  }

  const renderStepIcon = (completed: boolean, active: boolean, stepNumber: number) => (
    <ProgressStepIconExperimental
      status={getStepIconStatus(completed, active)}
      stepNumber={stepNumber}
    />
  )

  const renderStep = (step: Step, index: number) => {
    const textTheme = step.active || step.completed ? 'inherit' : 'muted'

    return (
      <Cell
        key={index}
        theme="transparent"
        prefix={renderStepIcon(step.completed, step.active, index + 1)}
        title={<Text as="span" text={step.title} theme={textTheme} testId={step.testId} />}
        body={step.description && <Text as="span" text={step.description} html theme={textTheme} />}
      />
    )
  }

  return <>{saleSteps.filter(Boolean).map(renderStep)}</>
}

export default ElectronicsSteps
